import { NavigationCollectionType, type IReaderViewAnnotation } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { useCirrusHighlightsStore } from "../stores/cirrusHighlights";
import type { ICirrusReaderHighlightData } from "../shared/CirrusReaderModel";

export const useCirrusHighlighting = () => {
    let hydrated = false;
    const highlightStore = useCirrusHighlightsStore();
    const cirrusMediaPlayerStore = useCirrusMediaPlayerStore();
    const cirrusReaderUiStore = useCirrusReaderUiStore();

    const { $cirrusReaderPublication, $cirrusReaderMediaPlayer } = useNuxtApp();
    
    const highlightsAnnotationLayer = useAnnotationLayer('cirrus-reader__highlights-layer', {
        key: 'publication-highlights',
        destroyOnLastUnmount: false,
        layerOptions: {
            annotationInputEngineEventsEnabled: true,
            layerStyle: { 'mix-blend-mode': 'multiply' },
        },
        defaultAnnotationOptions: {
            rangeClassName: 'cirrus-reader__highlights-layer',
            rangeStyle: {
                'background-color': '#FFDD55',
                'padding': '2px',
                'margin-top': '-2px',
                'margin-left': '-2px',
                'border-radius': '4px'
            }
        },
    });

    const initHighlightsLayer = async () => {
        if (hydrated) return;
        if (!$cirrusReaderPublication.colibrioReaderPublication) return;

        const publicationId = $cirrusReaderPublication.id;
        const highlights = highlightStore.highlights.filter(b => b.publicationId === publicationId);

        for (const h of highlights) {
            if (!highlightsAnnotationLayer.hasAnnotation(h.locator)) {
                const readerViewAnnotation: IReaderViewAnnotation = highlightsAnnotationLayer.createAnnotation(h.locator, h);
                readerViewAnnotation.setOptions({
                    rangeStyle: {
                        'background-color': h.color,
                    },
                });
            }
        }

        hydrated = true;
    };

    const addHighlight = async (highlightData: ICirrusReaderHighlightData): Promise<ICirrusReaderHighlightData | undefined> => {

        const locator = highlightData.locator;
        const contentLocation = await $cirrusReaderPublication.colibrioReaderPublication.fetchContentLocation(highlightData.locator);

        highlightData.publicationId = $cirrusReaderPublication.id;

        const navItemRefs = await contentLocation?.fetchNavigationItemReferences({
            collectionTypes: [NavigationCollectionType.TOC],
            greedy: true,
        });

        if (navItemRefs) {
            const navItemsInRange = navItemRefs.getItemsInRange();
            highlightData.bookSection = typeof navItemsInRange[navItemsInRange.length - 1] !== 'undefined' ?
                navItemsInRange[navItemsInRange.length - 1]?.getNavigationItem().getTextContent() :
                undefined;
        }

        if (locator) {
            const readerViewAnnotation: IReaderViewAnnotation = highlightsAnnotationLayer.createAnnotation(highlightData.locator, highlightData);
            readerViewAnnotation.setOptions({
                rangeStyle: {
                    'background-color': highlightData.color,
                },
            });
            useCirrusHighlightsStore().addHighlight(highlightData);
            return highlightData;
        } 
        return;
    }

    const updateHighlight = async (locator: string): Promise<void> => {
        const highlight = highlightStore.getHighlight(locator);
        if (highlight){
            const result = await useHighlightsModal().openModal({ highlight });
            if (result) {
                highlightStore.updateHighlight(locator, result);
                const readerViewAnnotation: IReaderViewAnnotation | undefined = highlightsAnnotationLayer.getAnnotation(locator);
                if (readerViewAnnotation){
                    readerViewAnnotation.setOptions({
                        rangeStyle: {
                            'background-color': result.color
                        }
                    })
                }
            }
        }
    }

    
    const deleteHighlight = async (locator: string): Promise<void> => {
        if (locator) highlightsAnnotationLayer.deleteAnnotation(locator);
        if (locator) useCirrusHighlightsStore().deleteHighlight(locator);
    }

    const highlightTextLocation = (
        textToHighlight: string,
        documentIndex: number,
        sourceHighlightData: ICirrusReaderHighlightData,
    ) => {
        useCirrusSearch().searchPublication(textToHighlight, 1, [documentIndex], false).then(async (resultSet) => {
            if (resultSet[0] && resultSet[0].locator) {
                sourceHighlightData.locator = resultSet[0].locator;
                addHighlight(sourceHighlightData).catch(console.warn);
            }
        }).catch(console.error);
        return;
    }

    const highlightReadingPosition = async (showEditDialog: boolean = true): Promise<void> => {
        const cirrusReaderNavigation = useCirrusReaderNavigation();
        
        if (cirrusReaderNavigation.readingPositionData && cirrusReaderNavigation.readingPositionData.contentBlockData && cirrusReaderNavigation.readingPositionData.contentBlockData.locator) {

            let locator = cirrusReaderNavigation.readingPositionData.contentBlockData.locator.toString();
            let locationText = cirrusReaderNavigation.readingPositionData.contentBlockData.textContent;

            if (cirrusMediaPlayerStore.playbackState && cirrusMediaPlayerStore.playbackState.segmentIndex){
                locator = (await $cirrusReaderMediaPlayer.fetchRangeContentLocationForSegmentIndex(cirrusMediaPlayerStore.playbackState.segmentIndex))?.getLocator().toString() || locator;
                locationText = cirrusMediaPlayerStore.playbackState?.text || locationText;
            }
            
            const highlight: ICirrusReaderHighlightData = {
                locator: locator,
                color: '#FFDD55',
                selectionText: locationText,
                comment: '',
            };

            if (showEditDialog) {
                const result = await useHighlightsModal().openModal({ highlight });
                if (result) addHighlight(result as ICirrusReaderHighlightData);
            } else {
                addHighlight(highlight);
            }

        }
    }

    const highlightClipboardContents = async (): Promise<void> => {
        const clipboardContents = await navigator.clipboard.readText();
        const cirrusReaderNavigation = useCirrusReaderNavigation();

        if (!clipboardContents) {
            return;
        }
        
        if (cirrusReaderNavigation.readingPositionData?.documentIndex && clipboardContents) {
            highlightTextLocation(clipboardContents, cirrusReaderNavigation.readingPositionData.documentIndex, {
                locator: '',
                color: '#FFDD55',
                selectionText: clipboardContents,
                comment: '',
            });
        }
    }

    const showHighlightDialogWithActiveSelectionState = async (): Promise<void>  => {
        const selectionSelector = cirrusReaderUiStore.getPopoverState()?.selectionLocatorData?.selectors[0];
        const selectionText = useCirrusReaderUiStore().getPopoverState()?.selectionText;

        if (!selectionSelector || !selectionText) return;

        const highlight: ICirrusReaderHighlightData = {
            locator: `#${selectionSelector}`,
            color: '#FFDD55',
            selectionText: selectionText,
            comment: '',
        };
        const result = await useHighlightsModal().openModal({ highlight });
        if (result) addHighlight(result as ICirrusReaderHighlightData);
    }


    return {
        initHighlightsLayer,
        addHighlight,
        updateHighlight,
        deleteHighlight,
        showHighlightDialogWithActiveSelectionState,
        highlightReadingPosition,
        highlightClipboardContents
    }
}